import React, { useEffect, useState } from "react";
import QuillEditor from "../../common/QuillEditor";
import { ReactComponent as CopyIcon } from "../../../assets/img/dashboard-svgs/copy.svg";
import { ReactComponent as CheckIcon } from "../../../assets/img/dashboard-svgs/checkmark.svg";
import { ReactComponent as TrashIcon } from "../../../assets/img/dashboard-svgs/ob-trash.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/img/dashboard-svgs/ob-download.svg";

const ChevSvg = () => (
  <svg className="section-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ICON_COLORS = ["violet", "cyan", "amber", "green", "rose"];
const FILE_TYPES = ["image", "video", "file_upload", "svg_image"];

const isConditionallyRequired = (field, formValues) => {
  if (!field.required_when_key) return false;
  const currentVal = formValues[field.required_when_key];
  return String(currentVal || "").trim() === String(field.required_when_value || "").trim();
};

const resolvedValue = (key, formValues, fieldMeta) => {
  const v = formValues[key];
  if (typeof v !== "undefined" && v !== null && v !== "") return v;
  const meta = fieldMeta[key];
  if (meta?.uploaded && typeof meta.uploaded === "string") return meta.uploaded;
  if (meta?.file_url && typeof meta.file_url === "string") return meta.file_url;
  return "";
};

const isTruthyValue = (value) => {
  if (typeof value === "boolean") return true;
  if (value === 0) return true;
  const str = String(value || "").trim();
  if (!str) return false;
  if (/<[a-z][\s\S]*>/i.test(str)) {
    const text = str.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim();
    return text.length > 0;
  }
  return !!str;
};

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return options.split(",").map((o) => o.trim()).filter(Boolean);
    }
  }
  return [];
};

const ChecklistSection = ({
  section,
  formValues = {},
  fieldMeta = {},
  updateField,
  handleUpload,
  requestDelete,
  savingByKey = {},
  savedByKey = {},
  errorByKey = {},
  isSuperAdmin = false,
  isFirstTime = false,
  focusedFieldKey = null,
}) => {
  const [openSections, setOpenSections] = useState(() =>
    (section?.subsections || []).slice(0, 2).map((s) => s.subsectionSlug)
  );
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    setOpenSections((section?.subsections || []).slice(0, 2).map((s) => s.subsectionSlug));
  }, [section?.sectionSlug, section?.subsections?.length]);

  useEffect(() => {
    if (focusedFieldKey) {
      const sub = (section?.subsections || []).find((s) =>
        (s.questions || []).some((q) => q.key === focusedFieldKey)
      );
      if (sub) {
        setOpenSections((prev) => {
          if (!prev.includes(sub.subsectionSlug)) {
            return [...prev, sub.subsectionSlug];
          }
          return prev;
        });
      }
    }
  }, [focusedFieldKey, section]);

  const toggleSection = (slug) =>
    setOpenSections((prev) =>
      prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]
    );

  const handleCopy = (key, text) => {
    const cleanText = String(text || "").replace(/<[^>]*>/g, "");
    navigator.clipboard.writeText(cleanText);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const allQuestions = (section?.subsections || []).flatMap((s) => s.questions || []);
  const totalQ = allQuestions.length;

  const isAnsweredQ = (field) => {
    const key = field.key;
    const meta = fieldMeta[key] || {};
    const val = resolvedValue(key, formValues, fieldMeta);
    if (FILE_TYPES.includes(field.input_type) || meta.uploaded || meta.file_url) {
      return !!(meta.uploaded || meta.file_url);
    }
    if (field.input_type === "yes_no") return val === "yes" || val === "no";
    if (typeof val === "object" && val !== null) return Object.keys(val).length > 0;
    return isTruthyValue(val);
  };

  const totalAns = allQuestions.filter(isAnsweredQ).length;
  const totalReq = allQuestions.filter((q) => {
    if (isFirstTime) {
      return q.first_time_required === 1 || q.first_time_required === true;
    }
    return !!q.is_required;
  }).length;
  const pendingReq = allQuestions.filter((q) => {
    if (isFirstTime) {
      return (q.first_time_required === 1 || q.first_time_required === true) && !isAnsweredQ(q);
    }
    return !!q.is_required && !isAnsweredQ(q);
  }).length;

  if (!section?.subsections?.length) {
    return <div className="gob-empty-state">No checklist items found.</div>;
  }

  return (
    <div>
      <div className="gob-breadcrumb">
        <span>Publisher Channel</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="current">Checklist</span>
      </div>

      <div className="gob-page-head">
        <h1 className="gob-page-title">Project <span className="gradient">Checklist</span></h1>
        <p className="gob-page-sub">
          Help us understand your project. Answer at your own pace — we will use this to set up your Publisher.
        </p>
      </div>

      <div className="gob-stats">
        <div className="stat-card"><div className="stat-label">Total questions</div><div className="stat-value">{totalQ}</div></div>
        <div className="stat-card"><div className="stat-label">Answered</div><div className="stat-value success">{totalAns}</div></div>
        <div className="stat-card"><div className="stat-label">Required pending</div><div className="stat-value warn">{pendingReq}</div></div>
        <div className="stat-card"><div className="stat-label">Sections</div><div className="stat-value">{section.subsections.length}</div></div>
      </div>

      {section.subsections.map((sub, idx) => {
        const isOpen = openSections.includes(sub.subsectionSlug);
        const qs = sub.questions || [];
        const answered = qs.filter(isAnsweredQ).length;
        const pct = qs.length > 0 ? (answered / qs.length) * 100 : 0;
        const fillCls = pct === 100 ? "" : pct > 0 ? " warn" : " empty";

        const isBasicsSubsection = sub.subsectionSlug?.toLowerCase() === "basics" ||
          sub.subsectionName?.toLowerCase() === "basics";

        return (
          <div key={sub.subsectionSlug} className={`section-card${isOpen ? " open" : ""}`}>
            <div className="section-head" onClick={() => toggleSection(sub.subsectionSlug)}>
              <div className={`section-icon ${ICON_COLORS[idx % ICON_COLORS.length]}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="section-title-block">
                <div className="section-title">{sub.subsectionName}</div>
                <div className="section-meta">{qs.length} question{qs.length !== 1 ? "s" : ""}</div>
              </div>
              <div className="section-progress">
                <div className="progress-track">
                  <div className={`progress-fill${fillCls}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="section-progress-text">{answered} / {qs.length}</span>
              </div>
              <ChevSvg />
            </div>

            <div className="section-body">
              {qs.map((field) => {
                const key = field.key;
                const done = isAnsweredQ(field);
                const val = resolvedValue(key, formValues, fieldMeta);
                const meta = fieldMeta[key] || {};
                const saving = !!savingByKey[key];
                const saved = !!savedByKey[key];
                const err = errorByKey[key] || "";
                const rawUrl = meta.uploaded || meta.file_url || "";
                const fileUrl = typeof rawUrl === "string" ? rawUrl : "";
                const hasValue = done || !!String(val || "").trim();

                const isInlineOptions = ["yes_no", "single_select"].includes(field.input_type);
                const isFullWidth = !isInlineOptions;
                const condRequired = !isFirstTime && isConditionallyRequired(field, formValues);
                const showRequired = isFirstTime
                  ? (field.first_time_required === 1 || field.first_time_required === true)
                  : (!!field.is_required || condRequired);

                return (
                  <div key={key} id={`q-row-${key}`} data-key={key} className={`q-row${done ? " answered" : ""}${isFullWidth ? " q-input-row" : ""}`}>
                    <div>
                      <div className="q-text" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                        <span>
                          {field.prompt}
                          {showRequired && <span className="gob-req-tag" style={{ marginLeft: 6 }}>Required</span>}
                        </span>

                        <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                          {hasValue && (field.input_type === "text" || field.input_type === "textarea" || (isBasicsSubsection && (field.input_type === "text" || field.input_type === "textarea"))) && (
                            <button
                              type="button"
                              onClick={() => handleCopy(key, val)}
                              className="gob-btn-icon"
                              title="Copy text"
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: copiedKey === key ? "var(--gob-success, #10b981)" : "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                            >
                              {copiedKey === key ? <CheckIcon style={{ width: 12, height: 12 }} /> : <CopyIcon style={{ width: 12, height: 12 }} />}
                            </button>
                          )}

                          {hasValue && FILE_TYPES.includes(field.input_type) && fileUrl && (
                            <a
                              href={fileUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="gob-btn-icon"
                              title="Download file"
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                            >
                              <DownloadIcon style={{ width: 12, height: 12 }} />
                            </a>
                          )}

                          {hasValue && field.input_type !== "yes_no" && field.input_type !== "single_select" && requestDelete && !isSuperAdmin && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => requestDelete(key)}
                              className="gob-btn-icon"
                              title="Clear value"
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#ef4444", opacity: saving ? 0.5 : 1, transition: "opacity 0.15s" }}
                            >
                              <TrashIcon style={{ width: 12, height: 12 }} />
                            </button>
                          )}
                        </div>
                      </div>
                      {field.tooltip && <div className="q-help">{field.tooltip}</div>}

                      {field.input_type === "html_editor" ? (
                        <div className="q-editor-container" style={{ marginTop: 8, background: "white", borderRadius: "6px" }}>
                          <QuillEditor
                            value={val}
                            onChange={(html) => updateField(field, html)}
                            onBlur={() => updateField(field, val, true)}
                            placeholder="Enter answer..."
                            readOnly={isSuperAdmin}
                          />
                        </div>
                      ) : isBasicsSubsection && (field.input_type === "text" || field.input_type === "textarea") && field.key !== "checklist_about_us_text" ? (
                        <div className="q-editor-container" style={{ marginTop: 8, background: "white", borderRadius: "6px" }}>
                          <QuillEditor
                            value={val}
                            onChange={(html) => updateField(field, html)}
                            onBlur={() => updateField(field, val, true)}
                            placeholder="Enter answer..."
                            readOnly={isSuperAdmin}
                          />
                        </div>
                      ) : (
                        <>
                          {/* Text input */}
                          {field.input_type === "text" && (
                            <input
                              className={`q-input${done ? " filled" : ""}`}
                              type="text"
                              value={val}
                              onChange={(e) => updateField(field, e.target.value)}
                              onBlur={(e) => updateField(field, e.target.value, true)}
                              placeholder="Enter answer..."
                              disabled={isSuperAdmin}
                            />
                          )}

                          {/* Textarea */}
                          {field.input_type === "textarea" && (
                            <textarea
                              className={`q-input${done ? " filled" : ""}`}
                              rows={3}
                              value={val}
                              onChange={(e) => updateField(field, e.target.value)}
                              onBlur={(e) => updateField(field, e.target.value, true)}
                              placeholder="Enter answer..."
                              style={{ resize: "vertical" }}
                              disabled={isSuperAdmin}
                            />
                          )}
                        </>
                      )}

                      {field.input_type !== "yes_no" && FILE_TYPES.includes(field.input_type) && (
                        <div style={{ marginTop: 8 }}>
                          {fileUrl ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {/\.(png|jpe?g|gif|svg|webp|bmp)$/i.test(fileUrl) && (
                                <img src={fileUrl} alt="Preview" style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 6, objectFit: "contain", border: "1px solid var(--gob-line)" }} />
                              )}
                              {/\.pdf$/i.test(fileUrl) && (
                                <iframe
                                  src={fileUrl}
                                  title="PDF preview"
                                  style={{ width: "100%", height: 200, border: "1px solid var(--gob-line)", borderRadius: 6 }}
                                />
                              )}
                              {/\.(txt|csv|log|json|xml|html|md)$/i.test(fileUrl) && (
                                <iframe
                                  src={fileUrl}
                                  title="Text preview"
                                  style={{ width: "100%", height: 160, border: "1px solid var(--gob-line)", borderRadius: 6, background: "#fafafa" }}
                                />
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 12, color: "var(--gob-success)", fontWeight: 600 }}>✓ Uploaded</span>
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="gob-btn"
                                  style={{ fontSize: 11, padding: "3px 10px" }}
                                >
                                  View
                                </a>
                              </div>
                            </div>
                          ) : isSuperAdmin ? (
                            <span style={{ fontSize: 12, color: "var(--gob-ink-3)" }}>No file uploaded</span>
                          ) : (
                            <label className="gob-btn" style={{ fontSize: 12, cursor: "pointer" }}>
                              {saving ? "Uploading..." : "Upload file"}
                              <input
                                type="file"
                                style={{ display: "none" }}
                                disabled={saving}
                                onChange={(e) => { if (e.target.files?.[0]) handleUpload(field, e.target.files[0]); }}
                              />
                            </label>
                          )}
                        </div>
                      )}

                      {saving && <div className="q-help" style={{ marginTop: 6 }}>Saving...</div>}
                      {saved && <div className="q-help" style={{ marginTop: 6, color: "var(--gob-success)" }}>Saved ✓</div>}
                      {err && <div style={{ marginTop: 6, fontSize: 12, color: "var(--gob-error)" }}>{err}</div>}
                    </div>

                    {isInlineOptions && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="q-options">
                          {field.input_type === "yes_no"
                            ? [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }].map((opt) => (
                              <button
                                key={opt.value}
                                className={`q-option${val === opt.value ? " selected yes" : ""}`}
                                disabled={isSuperAdmin}
                                onClick={() => !isSuperAdmin && updateField(field, opt.value, true)}
                              >
                                {opt.label}
                              </button>
                            ))
                            : parseOptions(field.options).map((opt) => {
                              const optVal = typeof opt === "object" ? opt.value ?? opt.label : opt;
                              const optLbl = typeof opt === "object" ? opt.label ?? opt.value : opt;
                              return (
                                <button
                                  key={optVal}
                                  className={`q-option${val === optVal ? " selected yes" : ""}`}
                                  disabled={isSuperAdmin}
                                  onClick={() => !isSuperAdmin && updateField(field, optVal, true)}
                                >
                                  {optLbl}
                                </button>
                              );
                            })}
                        </div>
                        {hasValue && requestDelete && !isSuperAdmin && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => requestDelete(key)}
                            className="gob-btn-icon"
                            title="Clear value"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#ef4444", opacity: saving ? 0.5 : 1, transition: "opacity 0.15s" }}
                          >
                            <TrashIcon style={{ width: 12, height: 12 }} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChecklistSection;
